import { useCurrentUser } from '@/lib/hooks/use-current-user';
import { CURRENT_WORKSPACE_ID } from '@/utils/local_storage_keys';
import { Role, Permission } from './types';
import { hasPermission, canManageUser, canAssignRole } from './permissions';
import { useState, useEffect } from 'react';

export const useIsSuperuser = (): boolean => {
  const { currentUser } = useCurrentUser();
  return currentUser?.isSuperuser ?? false;
};

export const useUserRole = (): Role => {
  const { currentUser } = useCurrentUser();

  // Track workspace ID changes
  const [workspaceId, setWorkspaceId] = useState(() =>
    localStorage.getItem(CURRENT_WORKSPACE_ID)
  );

  // Subscribe to workspace changes
  useEffect(() => {
    const handleWorkspaceChange = () => {
      setWorkspaceId(localStorage.getItem(CURRENT_WORKSPACE_ID));
    };

    // Listen for custom workspace change events
    window.addEventListener('workspaceChanged', handleWorkspaceChange);
    // Listen for storage changes from other tabs
    window.addEventListener('storage', (e) => {
      if (e.key === CURRENT_WORKSPACE_ID) {
        handleWorkspaceChange();
      }
    });

    return () => {
      window.removeEventListener('workspaceChanged', handleWorkspaceChange);
      window.removeEventListener('storage', handleWorkspaceChange);
    };
  }, []);

  if (!workspaceId || !currentUser) {
    return 'member'; // Default to most restrictive
  }

  // Superusers get implicit owner access to all workspaces
  if (currentUser.isSuperuser) {
    return 'owner';
  }

  // Get role from current user context
  // The role field is added by GraphQL: User.role(workspaceId)
  const backendRole = currentUser.role || 'member';

  // Normalize backend role to our RBAC role type.
  // Backend "user" maps to "member"; any other unknown value is treated as
  // "member" rather than blindly cast — a typo must never accidentally grant
  // writes, and read_only must be recognized explicitly to take effect.
  if (backendRole === 'user') {
    return 'member';
  }
  if (
    backendRole === 'read_only' ||
    backendRole === 'member' ||
    backendRole === 'admin' ||
    backendRole === 'owner'
  ) {
    return backendRole;
  }
  return 'member';
};

export const usePermission = (permission: Permission): boolean => {
  const role = useUserRole();
  return hasPermission(role, permission);
};

export const usePermissions = (permissions: Permission[]): Record<Permission, boolean> => {
  const role = useUserRole();
  return permissions.reduce((acc, p) => {
    acc[p] = hasPermission(role, p);
    return acc;
  }, {} as Record<Permission, boolean>);
};

export const useCanManageUser = (targetUserRole: Role): boolean => {
  const currentUserRole = useUserRole();
  return canManageUser(currentUserRole, targetUserRole);
};

export const useCanAssignRole = (targetRole: Role): boolean => {
  const currentUserRole = useUserRole();
  return canAssignRole(currentUserRole, targetRole);
};

// Convenience hooks for common checks
export const useIsOwner = (): boolean => {
  const role = useUserRole();
  return role === 'owner';
};

export const useIsAdminOrOwner = (): boolean => {
  const role = useUserRole();
  return role === 'admin' || role === 'owner';
};

export const useIsMember = (): boolean => {
  const role = useUserRole();
  return role === 'member';
};

export const useIsReadOnly = (): boolean => {
  const role = useUserRole();
  return role === 'read_only';
};

export const useCanMutate = (): boolean => {
  return usePermission('mutate:data');
};
